import React, { useState } from "react";
import { ScrollView } from "react-native";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import CheckBox from "@components/atoms/CheckBox";
import Spacer from "@components/atoms/Spacer";
import { SpaceType } from "@components/atoms/Spacer/types";
import styled from "styled-components/native";
import ShareImageCard from "@components/molecules/ShareImageCard";
import { ShareCheckBoxSectionProps, ShareType } from "../types";
import { VDBIcons } from "@utils/VDBIcons";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useCheckboxHandlers } from "../utils/utils";
import CertificateIcon from "@components/atoms/CertificateIcon";

const ShareCheckBoxSection = ({
  shareType,
  userInfo,
  priceChecked,
  setPriceChecked,
  imageItems,
  checkedItems,
  videoItems,
  reports,
  checkedReports,
  setCheckedItems,
  setBulkShareChecked,
  bulkShareChecked,
  itemDetail,
}: ShareCheckBoxSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [uriError, setUriError] = useState(false);
  const handlers = useCheckboxHandlers(setCheckedItems, checkedItems);

  const {
    images: { handleCheckboxChange, handleAllChecked, isAllChecked },
    reports: {
      handleCheckboxChange: handleReportCheckboxChange,
      handleAllChecked: handleAllReportChecked,
      isAllChecked: isAllReportsChecked,
    },
    videos: { handleAllChecked: handleVideoChecked },
  } = handlers;

  const handleBulkCheckboxChange = (type: keyof typeof bulkShareChecked) => {
    setBulkShareChecked((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <>
      {userInfo.price_mode !== PriceModes.GREEN && (
        <>
          <LineViewContainer background="core-02" />
          <ViewContainer>
            <CheckBox
              text={t("shareScreen.checkbox.price.text")}
              isChecked={priceChecked}
              onPress={setPriceChecked}
              testID="share.checkbox.price"
              dataTestId="share.checkbox.price"
              labelColor={priceChecked ? theme["core-05"] : theme["core-06"]}
              style={priceChecked ? typography["H3-M"] : typography["H3-R"]}
            />
          </ViewContainer>
        </>
      )}

      <LineViewContainer background="core-02" />
      {shareType === ShareType.singleShare ? (
        <>
          {imageItems.length > 0 && (
            <>
              <ViewContainer>
                <CheckBox
                  text={t("shareScreen.checkbox.images.text")}
                  isChecked={isAllChecked}
                  onPress={handleAllChecked}
                  testID="share.checkbox.images"
                  dataTestId="share.checkbox.images"
                  labelColor={
                    isAllChecked
                      ? theme["core-05"]
                      : imageItems.length === 0
                      ? theme["core-04"]
                      : theme["core-06"]
                  }
                  style={isAllChecked ? typography["H3-M"] : typography["H3-R"]}
                  disabled={imageItems.length === 0}
                />
              </ViewContainer>
              <ScrollViewContainer>
                <ScrollView horizontal={true}>
                  {imageItems?.map((item, index) => (
                    <React.Fragment key={index}>
                      <ShareImageCard
                        imageChecked={checkedItems.images[item.order]}
                        onPress={() => handleCheckboxChange(item.order)}
                        imgURL={uriError ? undefined : item.link}
                        onError={() => setUriError(true)}
                        genericIcon={uriError ? VDBIcons.IMAGE : undefined}
                      />
                      {index !== imageItems.length - 1 && (
                        <Spacer size={SpaceType.Basic} />
                      )}
                    </React.Fragment>
                  ))}
                </ScrollView>
              </ScrollViewContainer>
            </>
          )}
        </>
      ) : (
        <>
          <ViewContainer>
            <CheckBox
              text={t("shareScreen.checkbox.images.text")}
              isChecked={bulkShareChecked.bulkImageShare}
              onPress={() => handleBulkCheckboxChange("bulkImageShare")}
              testID="share.checkbox.bulkImages"
              dataTestId="share.checkbox.bulkImages"
              labelColor={
                bulkShareChecked.bulkImageShare
                  ? theme["core-05"]
                  : theme["core-06"]
              }
              style={
                bulkShareChecked.bulkImageShare
                  ? typography["H3-M"]
                  : typography["H3-R"]
              }
            />
          </ViewContainer>
        </>
      )}
      <LineViewContainer background="core-02" />
      {(videoItems.length > 0 || shareType !== ShareType.singleShare) && (
        <ViewContainer>
          <CheckBox
            text={t("shareScreen.checkbox.videos.text")}
            isChecked={checkedItems.videos}
            onPress={() => handleVideoChecked()}
            testID="share.checkbox.videos"
            dataTestId="share.checkbox.videos"
            labelColor={
              checkedItems.videos
                ? theme["core-05"]
                : shareType === ShareType.singleShare && videoItems.length === 0
                ? theme["core-04"]
                : theme["core-06"]
            }
            style={
              checkedItems.videos ? typography["H3-M"] : typography["H3-R"]
            }
            disabled={
              shareType === ShareType.singleShare && videoItems.length === 0
            }
          />
        </ViewContainer>
      )}
      <LineViewContainer background="core-02" />
      {shareType === ShareType.singleShare ? (
        <>
          {reports.length > 0 && (
            <>
              <ViewContainer>
                <CheckBox
                  text={t("shareScreen.checkbox.reports.text")}
                  isChecked={isAllReportsChecked}
                  onPress={handleAllReportChecked}
                  testID="share.checkbox.reports"
                  dataTestId="share.checkbox.reports"
                  labelColor={
                    isAllReportsChecked
                      ? theme["core-05"]
                      : reports.length === 0
                      ? theme["core-04"]
                      : theme["core-06"]
                  }
                  style={
                    isAllReportsChecked
                      ? typography["H3-M"]
                      : typography["H3-R"]
                  }
                  disabled={reports.length === 0}
                />
              </ViewContainer>
              <ScrollViewContainer>
                <ScrollView horizontal={true}>
                  {reports?.map((item, index) => (
                    <React.Fragment key={index}>
                      <ShareImageCard
                        imageChecked={checkedReports[item.order]}
                        onPress={() => handleReportCheckboxChange(item.order)}
                        imgURL={undefined}
                        customIcon={
                          <CertificateIcon
                            mediaType={item.type}
                            labId={itemDetail?.lab}
                            size={60}
                            color={theme["core-06"]}
                          />
                        }
                      />
                      {index !== reports.length - 1 && (
                        <Spacer size={SpaceType.Basic} />
                      )}
                    </React.Fragment>
                  ))}
                </ScrollView>
              </ScrollViewContainer>
            </>
          )}
        </>
      ) : (
        <>
          <ViewContainer>
            <CheckBox
              text={t("shareScreen.checkbox.reports.text")}
              isChecked={bulkShareChecked.bulkReportsShare}
              onPress={() => handleBulkCheckboxChange("bulkReportsShare")}
              testID="share.checkbox.bulkReports"
              dataTestId="share.checkbox.bulkReports"
              labelColor={
                bulkShareChecked.bulkReportsShare
                  ? theme["core-05"]
                  : theme["core-06"]
              }
              style={
                bulkShareChecked.bulkReportsShare
                  ? typography["H3-M"]
                  : typography["H3-R"]
              }
            />
          </ViewContainer>
        </>
      )}
    </>
  );
};

const ViewContainer = styled.View`
  padding: ${SpaceType.Double}px;
`;

const ScrollViewContainer = styled.View`
  padding-top: ${SpaceType.ExtraSmall}px;
  padding-bottom: ${SpaceType.Big}px;
  padding-left: ${SpaceType.Double}px;
`;

export default ShareCheckBoxSection;
